<template>
  <a-modal :width="600" :title="$t('supplier.add_title')" :visible="visible" :closable="false">
    <a-spin :spinning="fetchingCreateSupplier" :tip="$t('supplier.creating_tip')">
      <template>
        <a-form-model
          ref="ruleForm"
          :model="form"
          :rules="rules"
          :label-col="{ span: 6 }"
          :wrapper-col="{ span: 18 }"
        >
          <a-form-model-item
            :label="$t('supplier.form.code')"
            prop="supplierCode"
          >
            <a-input
              v-model="form.supplierCode"
              :placeholder="$t('supplier.placeholder.code')"
              :max-length="60"
            />
          </a-form-model-item>
          <a-form-model-item
            :label="$t('supplier.form.name')"
            prop="supplierName"
          >
            <a-input
              v-model="form.supplierName"
              :placeholder="$t('supplier.placeholder.name')"
              :max-length="60"
            />
          </a-form-model-item>
          <a-form-model-item
            :label="$t('supplier.form.city')"
            prop="cityName"
          >
            <a-input
              v-model="form.cityName"
              :placeholder="$t('supplier.placeholder.city')"
              :max-length="60"
            />
          </a-form-model-item>
          <a-form-model-item
            :label="$t('supplier.form.address')"
            prop="address"
          >
            <a-textarea
              v-model="form.address"
              :placeholder="$t('supplier.placeholder.address')"
              rows="3"
            />
          </a-form-model-item>
          <a-form-model-item
            :label="$t('supplier.form.email')"
            prop="email"
          >
            <a-input
              v-model.trim="form.email"
              autocomplete="off"
              :placeholder="$t('supplier.placeholder.email')"
            />
          </a-form-model-item>
          <a-form-model-item
            :label="$t('supplier.form.phone')"
            prop="phone"
          >
            <a-input
              v-model.trim="form.phone"
              :placeholder="$t('supplier.placeholder.phone')"
              :max-length="60"
              @keypress="numberOnly"
            />
          </a-form-model-item>
        </a-form-model>
      </template>
    </a-spin>
    <template #footer>
      <template>
        <a-button
          :loading="fetchingCreateSupplier"
          @click="closeModal"
        >
          {{ $t('utils.cancel') }}
        </a-button>
        <a-button
          type="primary"
          :loading="fetchingCreateSupplier"
          @click="submitFormAddSupplier"
        >
          {{ $t('supplier.add_supplier') }}
        </a-button>
      </template>
    </template>
  </a-modal>
</template>

<script>
import { createSupplier } from '@/api/purchase-order'
import debounce from 'lodash/debounce'

const validatorField = (rule, value, callback, params, i18n) => {
  if ((value === null || value === '') && rule.required) {
    callback(new Error(i18n.t('supplier.rules.required', { fieldName: params.fieldName })))
  } else if (value.length > params.max) {
    callback(new Error(i18n.t('supplier.rules.max', { fieldName: params.fieldName, max: params.max })))
  } else {
    callback()
  }
}

export default {
  name: 'ModalAddSupplier',
  components: {
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    businessId: {
      type: String,
      default: '',
    },
  },
  data() {
    let checkEmail = (rule, value, callback) => {
      if (value === '') {
        callback()
      } else {
        const mailformat = /^\w+([\\.+-]?\w+)*@\w+([\\.+-]?\w+)*(\.\w{1,10})+$/
        if (value.match(mailformat)) {
          callback()
        } else {
          callback(new Error(`${this.$t('customer.rules.email_invalid')}`))
        }
      }
    }
    
    return {
      fetchingCreateSupplier: false,
      form: {
        supplierCode: '',
        supplierName: '',
        cityName: '',
        address: '',
        email: '',
        phone: '',
      },
      rules: {
        supplierCode: [{
          required: true,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            validatorField(rule, value, callback, { fieldName: this.$t('supplier.form.code'), max: 60 }, this.$i18n)
          },
        }],
        supplierName: [{
          required: true,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            validatorField(rule, value, callback, { fieldName: this.$t('supplier.form.name'), max: 60 }, this.$i18n)
          },
        }],
        cityName: [{
          required: false,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            validatorField(rule, value, callback, { fieldName: this.$t('supplier.form.city'), max: 60 }, this.$i18n)
          },
        }],
        address: [{
          required: false,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            validatorField(rule, value, callback, { fieldName: this.$t('supplier.form.address'), max: 1000 }, this.$i18n)
          },
        }],
        email: [{
          required: false,
          trigger: 'blur',
          validator: checkEmail,
        }],
        phone: [{
          required: false,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            validatorField(rule, value, callback, { fieldName: this.$t('supplier.form.phone'), max: 25 }, this.$i18n)
          },
        }],
      },
    }
  },
  computed: {
  },
  watch: {
    visible: {
      deep: true,
      immediate: false,
      handler(val) {
        if (!val) {
          this.$refs.ruleForm.resetFields()
        }
      },
    },
  },
  methods: {
    numberOnly(event) {
      const keyCode = (event.which) ? event.which : event.keyCode
      if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) event.preventDefault()
    },
    closeModal() {
      this.$emit('onCancel', false)
    },
    submitFormAddSupplier() {
      this.$refs.ruleForm.validate((valid) => {
        if (valid) {
          if (!this.businessId) {
            return this.$notification.error({
              message: this.$t('supplier.error_business_id'),
            })
          }
          this.fetchingCreateSupplier = true
          this.onSaveSupplier()
        }
      })
    },
    onSaveSupplier: debounce(async function() {
      const {
        supplierCode,
        supplierName,
        cityName,
        address,
        email,
        phone,
      } = this.form
      const payload = {
        id: null,
        code: supplierCode,
        name: supplierName,
        city: cityName,
        address: address,
        email: email,
        phone: phone,
        business_id: this.businessId,
      }
      createSupplier({
        data: payload,
        business_id: this.businessId,
      })
      .then(() => {
        this.$notification.success({
          message: this.$t('supplier.success_add'),
        })
        this.fetchingCreateSupplier = false
        this.$emit('onCancel', true)
      })
      .catch(() => {
        this.$notification.error({
          message: this.$t('supplier.error_add'),
        })
        this.fetchingCreateSupplier = false
      })
      this.fetchingCreateSupplier = false
    }, 1000),
  },
}
</script>

<style lang="scss" module>
</style>
<style lang="scss">
.image-spin {
  -webkit-animation:spin 4s linear infinite;
  -moz-animation:spin 4s linear infinite;
  animation:spin 4s linear infinite;
}
@-moz-keyframes spin { 
    100% { -moz-transform: rotate(360deg); } 
}
@-webkit-keyframes spin { 
    100% { -webkit-transform: rotate(360deg); } 
}
@keyframes spin { 
    100% { 
        -webkit-transform: rotate(360deg); 
        transform:rotate(360deg); 
    } 
}
</style>